import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Card, CardSm } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { PaperPlaneTilt, ChatCircleDots } from '@phosphor-icons/react';

const QUICK = [
  'How do I measure DBH on a multi-stem mangrove?',
  'What GPS accuracy is required for VM0033 plot establishment?',
  'Which allometric equation should I use for Avicennia marina?',
  'Difference between PSP and inventory plot?',
];

function genSession() {
  let s = localStorage.getItem('mg_chat_session');
  if (!s) { s = 'sess_' + Math.random().toString(36).slice(2); localStorage.setItem('mg_chat_session', s); }
  return s;
}

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const session = useRef(genSession());
  const listEnd = useRef(null);

  useEffect(() => {
    api.get(`/ai/chat/${session.current}`).then((r) => setMessages(r.data || []));
  }, []);

  useEffect(() => {
    listEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    if (!text.trim()) return;
    setBusy(true); setErr('');
    setMessages((m) => [...m, { role: 'user', content: text, _local: true, id: 'l' + Date.now() }]);
    setInput('');
    try {
      const { data } = await api.post('/ai/chat', {
        session_id: session.current, message: text,
      });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, id: 'r' + Date.now() }]);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'AI chat failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="ai-assistant" className="flex flex-col" style={{ minHeight: 'calc(100dvh - 72px)' }}>
      <TopBar title="Field Assistant" />

      <div className="flex-1 p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <CardSm>
            <div className="flex items-center gap-2 mb-2">
              <ChatCircleDots size={18} weight="bold" />
              <span className="font-display font-bold uppercase tracking-widest text-xs">
                MangroveGuard AI
              </span>
            </div>
            <p className="text-sm">
              Ask me anything about VM0033 protocols, allometric equations, multi-stem DBH,
              GPS standards, or species ID rules. I keep answers brief and field-ready.
            </p>
          </CardSm>
        )}

        {messages.map((m) => (
          <div key={m.id || m.created_at} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] border-2 border-carbon px-3 py-2 ${
                m.role === 'user' ? 'bg-carbon text-canvas' : 'bg-white'
              }`}
              data-testid={m.role === 'user' ? 'msg-user' : 'msg-assistant'}
            >
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</p>
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex justify-start">
            <div className="bg-white border-2 border-carbon px-3 py-2 mono text-sm pulse-ring">
              thinking…
            </div>
          </div>
        )}

        {err && <div className="border-2 border-status-severe bg-red-50 px-3 py-2 text-xs text-status-severe">{err}</div>}

        <div ref={listEnd} />
      </div>

      {/* Quick replies */}
      <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="quick-replies">
        {QUICK.map((q) => (
          <button key={q}
                  onClick={() => send(q)}
                  className="text-xs px-2 py-1 border-2 border-carbon bg-surface-alt hover:bg-white font-display font-bold uppercase tracking-widest"
                  data-testid="quick-reply"
          >
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="border-t-2 border-carbon p-3 flex gap-2 bg-canvas"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the field assistant…"
          className="input-rugged font-sans"
          data-testid="chat-input"
        />
        <button
          type="submit"
          className="min-w-[56px] bg-carbon text-canvas border-2 border-carbon btn-hard"
          disabled={busy}
          data-testid="chat-send"
          aria-label="Send"
        >
          <PaperPlaneTilt size={22} weight="bold" />
        </button>
      </form>
    </div>
  );
}
